/**
 * Brand-agnostic design-token SCALES (SSOT). CANONICAL ELSEWHERE -- do not re-add as aliases: border radius + box shadow (design-tokens-core.ts); opacity + motion (const/interaction-data.ts).
 * DOES NOT BELONG HERE (brand-specific): colors, font families, gradients, component shape rules, focus rings, backdrops.
 */

/** 4px grid: each numeric key is its px value / 4. Named keys are listed below. */
export const spacingScale = {
	0: '0',
	0.25: '0.0625rem',
	0.5: '0.125rem',
	0.75: '0.1875rem',
	1: '0.25rem',
	1.25: '0.3125rem',
	1.5: '0.375rem',
	2: '0.5rem',
	2.5: '0.625rem',
	3: '0.75rem',
	3.5: '0.875rem',
	4: '1rem',
	4.5: '1.125rem',
	5: '1.25rem',
	6: '1.5rem',
	7: '1.75rem',
	8: '2rem',
	9: '2.25rem',
	10: '2.5rem',
	11: '2.75rem',
	12: '3rem',
	14: '3.5rem',
	15: '3.75rem',
	16: '4rem',
	18: '4.5rem',
	20: '5rem',
	24: '6rem',
	25: '6.25rem',
	30: '7.5rem',
	32: '8rem',
	35: '8.75rem',
	37.5: '9.375rem',
	40: '10rem',
	44: '11rem',
	45: '11.25rem',
	48: '12rem',
	50: '12.5rem',
	60: '15rem',
	62.5: '15.625rem',
	70: '17.5rem',
	75: '18.75rem',
	80: '20rem',
	90: '22.5rem',
	96: '24rem',
	100: '25rem',
	105: '26.25rem',
	120: '30rem',
	125: '31.25rem',
	150: '37.5rem',
	175: '43.75rem',
	200: '50rem',
	'212.5': '53.125rem',
	225: '56.25rem',
	300: '75rem',
	350: '87.5rem',

	xxs: '0.125rem', // 2px
	xs: '0.5rem', // 8px
	sm: '0.75rem', // 12px
	md: '1rem', // 16px
	lg: '1.5rem', // 24px
	xl: '2rem', // 32px
} as const;

export const borderWidthScale = {
	DEFAULT: '1px',
	0: '0',
	2: '2px',
	4: '4px',
	8: '8px',
} as const;

/**
 * The weight ramp, on the CSS 100-step stride.
 *
 * `extrabold` AND `black` ADMITTED 2026-08-29, and the half-step objection recorded on `lineHeightScale` and
 * `fontSizeScale` does NOT reach them: those refusals were about squeezing new rungs INSIDE a band the ramp already
 * divides, and 800/900 CONTINUE the stride past its top rather than subdividing anything. Three sites were already
 * rendering them as bare numerics (the title report's letterhead wordmark and its two verdict figures), so this is the
 * scale catching up with what the tree draws, and the one loaded face resolves them: Inter ships the full range.
 */
export const fontWeightScale = {
	light: '300',
	normal: '400',
	regular: '400',
	medium: '500',
	semibold: '600',
	bold: '700',
	extrabold: '800',
	black: '900',
} as const;

/** rem-based type ramp. */
export const fontSizeScale = {
	'2xs': '0.625rem', // 10px
	xs: '0.75rem', // 12px
	sm: '0.875rem', // 14px
	base: '1rem', // 16px
	lg: '1.125rem', // 18px
	xl: '1.25rem', // 20px
	'2xl': '1.5rem', // 24px
	'3xl': '1.875rem', // 30px
	'4xl': '2.25rem', // 36px
	'5xl': '3rem', // 48px
	'6xl': '3.75rem', // 60px
	'7xl': '4.5rem', // 72px
	'8xl': '6rem', // 96px
} as const;

/**
 * Leading ramp, spelled as STRINGS so a consumer reads one type off every rung.
 *
 * NO `hero: '1.0'` RUNG (DL-1.13, 2026-08-20). It and `none: '1'` were the SAME magnitude written two ways, which made
 * `--line-height-hero` and `--line-height-none` byte-different names for one decision and let a surface pick a
 * "spelling" instead of a leading. `hero` had zero consumers on every channel, so it is the half that goes; the one
 * site that meant it (`heroStyle` in brand/federa/tokens/typography.ts, which spelled the numeric `1.0` rather than
 * reading either rung) now reads `none`.
 *
 * 1.55 AND 1.6 ARE DELIBERATELY NOT RUNGS, recorded here so the question is not re-opened per site. Twelve raw
 * literals in the tree carry them (five and seven), and both sit inside the 0.25 band this scale already divides with
 * `normal` (1.5) and `relaxed` (1.75). Admitting them would put four rungs inside a 0.25 band, which is a ramp that no
 * longer decides anything -- the correct cure is adopting `normal` or `relaxed` at each site, which is a per-surface
 * visual call rather than a sweep, and is tracked as such.
 */
export const lineHeightScale = {
	none: '1',
	snug: '1.1',
	tight: '1.2',
	normal: '1.5',
	default: '1.4',
	relaxed: '1.75',
} as const;

export const letterSpacingScale = {
	hero: '-0.02em',
	primary: '-0.01em',
	tight: '-0.025em',
	normal: '0',
	tertiary: '0.01em',
	wide: '0.025em',
	uppercase: '0.05em',
	/** Display/eyebrow tracking rung: the band the uppercase eyebrow labels across navigator, market-maker and the title pages were re-deriving inline as 0.08em-0.2em literals. */
	widest: '0.1em',
} as const;

/**
 * RELATIVE type sizes: a face that must stay a FRACTION of the lockup it sits in, so it follows that lockup down a tier
 * instead of standing on the rem ramp. An em value by design -- it resolves against the font size where it is used.
 * `heroAside` is the hero figure's sign, unit and tenth: about a third of the figure (Noor's first screen and meter,
 * iss-5c6a38c6-7b6d-4486-a80e-6b452b6cbe0a, which spelled it inline because no token carried it).
 */
export const relativeFontSizeScale = {
	heroAside: '0.35em',
} as const;

/** Layer hierarchy. Rungs are strictly increasing; adopt the nearest rung rather than hardcoding a numeric at a call site. Values match what adopting call sites already rendered, so adoption is a rename and never a restack. */
export const zIndexScale = {
	base: 0,
	raised: 1,
	chip: 2,
	surface: 10,
	/** Above `surface` sticky chrome (e.g. a panel covering a `sticky` table head). */
	surfaceOverlay: 20,
	/** Scrim rendered directly beneath a `floating` panel. */
	floatingBackdrop: 40,
	floating: 50,
	/** Scrim/layer that must cover a `floating` affordance (e.g. a bottom-sheet backdrop over a fixed mobile nav). */
	floatingRaised: 60,
	/** Panel that must clear its own `floatingRaised` scrim. */
	floatingRaisedMax: 61,
	affix: 100,
	backTop: 999,
	dropdown: 1000,
	dropdownRaised: 1001,
	dropdownMax: 1002,
	sticky: 1100,
	fixed: 1200,
	modalBackdrop: 1300,
	modal: 1400,
	popover: 1500,
	toast: 1600,
	tooltip: 1700,
	loadingOverlay: 1800,
	devTools: 9998,
	max: 9999,
	overlayMax: 10000,
} as const;

/**
 * The px RUNGS every breakpoint spelling below derives from -- numeric, because both emitted shapes (the `Npx` string
 * scale and the pre-parsed numeric scale) plus the max-width companions are arithmetic over the same decision, and
 * spelling `991px` beside `992px` is that decision written twice. It was written twice, and the two drifted: `lg`
 * moved to Tailwind's 1024px on 2026-08-20 while a hand-written `tabletMax: '991px'` would have stayed a rung behind
 * a boundary that no longer existed. Add a rung here, not below.
 *
 * `lg` IS 1024px, reconciled 2026-08-20 (operator adjudication). It had been 992px against Tailwind's own 1024px, and
 * Tailwind's is what the tree actually renders: the `lg:` responsive variants compile from Tailwind's default scale at
 * BUILD time and outnumber this token's runtime readers by two orders of magnitude, so the token adopted the rendered
 * value rather than the reverse. The remaining forks (`sm` 480px vs Tailwind's 40rem/640px) stand and are NOT drift --
 * see the scale docstring below.
 */
const BREAKPOINT_PX_RUNGS = {
	sm: 480,
	md: 768,
	lg: 1024,
	xl: 1280,
	'2xl': 1536,
} as const;

/** `Npx` spelling of a rung. */
const breakpointPx = (value: number): string => `${String(value)}px`;

/**
 * TWO breakpoint scales are live on purpose, and this one governs only the `var()`/JS consumers. These values emit
 * as `--breakpoint-*` custom properties in base.css `:root`, where they SHADOW Tailwind's same-named theme tokens
 * for every runtime `var()` read -- but Tailwind's responsive VARIANTS (`sm:` etc.) compile at BUILD time from its
 * own default scale (`sm` 40rem/640px vs this scale's 480px), which media queries cannot read vars from. Moving
 * these names into the base.css `@theme` block WOULD retune every compiled variant boundary fleet-wide -- that is a
 * design decision, not a dedup; `readTailwindOwnedThemeNames` (`tools/design-tokens/generate-css-vars.ts`) excludes
 * `--breakpoint-*` from the @theme handoff for exactly this reason.
 *
 * The `*Max` members are the MAX-WIDTH companions of their rungs, one pixel below so a `max-width` band and the
 * `min-width` band above it cannot both be true at the same pixel. Derived, never typed: the collision they exist to
 * prevent is exactly what a hand-written companion reintroduces the moment its rung moves.
 * `DS-BREAKPOINT-MAGNITUDE-LITERAL-001` builds its allowed set from this object, so a rung added here widens that
 * rule with no edit there.
 */
export const breakpointScale = {
	mobileMax: breakpointPx(BREAKPOINT_PX_RUNGS.sm - 1),
	tabletMax: breakpointPx(BREAKPOINT_PX_RUNGS.lg - 1),
	sm: breakpointPx(BREAKPOINT_PX_RUNGS.sm),
	md: breakpointPx(BREAKPOINT_PX_RUNGS.md),
	lg: breakpointPx(BREAKPOINT_PX_RUNGS.lg),
	xl: breakpointPx(BREAKPOINT_PX_RUNGS.xl),
	'2xl': breakpointPx(BREAKPOINT_PX_RUNGS['2xl']),
} as const;

/** Pre-parsed numeric breakpoints for the JS-side viewport predicates (`matchMedia` and `innerWidth` cannot resolve a
 *  `var()`, so they need the number). Read straight off the rungs -- the `Number.parseInt` round-trip through the
 *  string scale this replaced parsed a value it had just formatted. */
export const breakpointScaleNumeric = {
	sm: BREAKPOINT_PX_RUNGS.sm,
	md: BREAKPOINT_PX_RUNGS.md,
	lg: BREAKPOINT_PX_RUNGS.lg,
	xl: BREAKPOINT_PX_RUNGS.xl,
} as const;
